/**
 * Cliente HTTP da API.
 *
 * Concentra três coisas que, espalhadas, viram bug: a base da URL, o cabeçalho
 * de credencial e a validação da resposta com Zod. Nenhum componente monta
 * `fetch` na mão.
 */
import type { z } from "zod";
import {
  auditEmailSchema,
  auditResponseSchema,
  baseListSchema,
  baseSchema,
  clientSchema,
  clientSettingsSchema,
  deviceLinksResponseSchema,
  driverListSchema,
  driverSchema,
  garageListSchema,
  okSchema,
  profileListSchema,
  registryVehicleListSchema,
  registryVehicleSchema,
  userListSchema,
  userSchema,
  ecoProfileListSchema,
  ecoProfileSchema,
  ecoRuleListSchema,
  vehicleModelListSchema,
  vehicleModelSchema,
  type DeviceLinksResponse,
  fleetSnapshotSchema,
  historyResponseSchema,
  loginResponseSchema,
  meSchema,
  refreshResponseSchema,
  alertsResponseSchema,
  geocodeSchema,
  geofenceListSchema,
  geofenceSchema,
  summaryResponseSchema,
  vehicleListSchema,
  alertKindsSchema,
  channelSchema,
  channelsSchema,
  channelTestSchema,
  communicationSchema,
  deliveriesSchema,
  queueResponseSchema,
  retrySchema,
  tripsResponseSchema,
  treatmentHistorySchema,
  fleetIndicatorsSchema,
  trafficDailySchema,
  trafficMonthlySchema,
} from "./schemas.ts";

const API_URL = (import.meta.env.VITE_API_URL ?? "").replace(/\/+$/, "");

/** Erro com status: quem chama distingue 401 de "a rede caiu". */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * A API respondeu, mas o corpo não tem a forma declarada.
 *
 * Separado de `ApiError` porque a CAUSA é outra e o conserto fica em outro
 * lugar: 401 é credencial, 0 é rede, e isto aqui é contrato — a API mandou um
 * campo com tipo diferente do que o painel declara. Em 02/11 custou uma tarde:
 * `/fleet/communication` respondia 200 com `silentHours: "17.3"` (string, de um
 * `round(numeric)` no SQL) e a tela dizia só "Não foi possível carregar".
 *
 * `issues` carrega o CAMPO, que é a única coisa que importa para quem vai
 * consertar.
 */
export class ApiShapeError extends ApiError {
  constructor(
    status: number,
    readonly path: string,
    readonly issues: readonly string[],
  ) {
    super(status, `resposta inesperada da API em ${path}: ${issues.join("; ")}`);
    this.name = "ApiShapeError";
  }
}

export function getApiUrl(): string {
  return API_URL;
}

/** `https://api...` → `wss://api.../stream`. O WS não passa por CORS. */
export function getStreamUrl(token: string): string {
  const base = API_URL || window.location.origin;
  const url = new URL("/stream", base);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.searchParams.set("token", token);
  return url.toString();
}

interface RequestOptions {
  token?: string | undefined;
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal | undefined;
}

async function request<T>(path: string, schema: z.ZodType<T>, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.token) headers.Authorization = `Bearer ${options.token}`;
  if (options.body !== undefined) headers["Content-Type"] = "application/json";

  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      method: options.method ?? "GET",
      headers,
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch {
    // `fetch` que estoura sem status é rede ou CORS — não é a API respondendo.
    // Dizer isso poupa meia hora procurando erro no lugar errado.
    throw new ApiError(0, "não foi possível falar com a API (rede ou CORS)");
  }

  if (!response.ok) {
    throw new ApiError(response.status, await readErrorMessage(response));
  }

  // Validar aqui, e não onde o dado é usado: um campo faltando vira erro no
  // ponto em que dá para explicar, em vez de `undefined` atravessando a tela.
  const parsed = schema.safeParse(await response.json());
  if (!parsed.success) {
    // O caminho e o motivo de cada campo: `vehicles.0.silentHours: expected
    // number, received string`. Sem isto, a tela diz que falhou e NÃO diz o
    // quê — e a requisição aparece 200 com dados no painel do navegador, que é
    // o estado mais caro de diagnosticar que existe.
    const issues = parsed.error.issues
      .slice(0, 3)
      .map((issue) => `${issue.path.join(".") || "(raiz)"}: ${issue.message}`);
    console.warn(`[api] forma inesperada em ${path}`, parsed.error.issues);
    throw new ApiShapeError(response.status, path, issues);
  }
  return parsed.data;
}

/**
 * Resposta que NÃO é JSON — hoje só o CSV da auditoria.
 *
 * Não dá para baixar com `<a href>`: a credencial vai no cabeçalho
 * `Authorization`, e âncora não manda cabeçalho. Então o arquivo vem por
 * `fetch` e vira `Blob` no navegador — que é também o que mantém o download
 * dentro da mesma trava de papel do resto da API.
 */
async function requestText(path: string, options: RequestOptions = {}): Promise<string> {
  const headers: Record<string, string> = {};
  if (options.token) headers.Authorization = `Bearer ${options.token}`;

  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, { headers, ...(options.signal ? { signal: options.signal } : {}) });
  } catch {
    throw new ApiError(0, "não foi possível falar com a API (rede ou CORS)");
  }
  if (!response.ok) throw new ApiError(response.status, await readErrorMessage(response));
  return response.text();
}

async function readErrorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { message?: string | string[] };
    const message = Array.isArray(body.message) ? body.message.join("; ") : body.message;
    return message ?? `erro ${response.status}`;
  } catch {
    return `erro ${response.status}`;
  }
}

export const api = {
  login: (email: string, password: string) =>
    request("/auth/login", loginResponseSchema, { method: "POST", body: { email, password } }),

  /** Quem a credencial atual representa. É daqui que o papel volta depois de um F5. */
  me: (token: string, signal?: AbortSignal) =>
    request("/auth/me", meSchema, { token, ...(signal ? { signal } : {}) }),

  refresh: (token: string) => request("/auth/refresh", refreshResponseSchema, { method: "POST", token }),

  fleetPositions: (token: string, signal?: AbortSignal) =>
    request("/fleet/positions", fleetSnapshotSchema, { token, ...(signal ? { signal } : {}) }),

  /**
   * Último registro do equipamento, com os `extras`.
   *
   * `order=desc&limit=1` é o atalho que evita paginar até o fim da janela. E o
   * `to` vai uma hora no futuro de propósito: a hora do pacote é o relógio do
   * EQUIPAMENTO e pode vir adiantada — sem a folga, um equipamento à frente não
   * teria registro "mais recente".
   */
  latestTelemetry: (token: string, imei: string, signal?: AbortSignal) => {
    const to = new Date(Date.now() + 3_600_000).toISOString();
    const from = new Date(Date.now() - 7 * 86_400_000).toISOString();
    const query = `from=${from}&to=${to}&limit=1&order=desc&includeExtras=true`;
    return request(`/devices/${imei}/telemetry?${query}`, historyResponseSchema, {
      token,
      ...(signal ? { signal } : {}),
    });
  },

  /**
   * Resumo do dia. É a ÚNICA fonte honesta de horímetro: não existe coluna de
   * horas de motor — a API deriva dos ciclos de ignição, e é por isso que o
   * painel rotula "hoje" em vez de fingir um horímetro de vida inteira.
   */
  summaryToday: (token: string, imei: string, signal?: AbortSignal) => {
    const to = new Date(Date.now() + 3_600_000).toISOString();
    const from = new Date(new Date().setHours(0, 0, 0, 0)).toISOString();
    return api.summary(token, imei, from, to, "day", signal);
  },

  /** Resumo de um período qualquer — a base do relatório. */
  summary: (
    token: string,
    imei: string,
    from: string,
    to: string,
    granularity: "day" | "hour",
    signal?: AbortSignal,
  ) =>
    request(
      `/devices/${imei}/summary?from=${from}&to=${to}&granularity=${granularity}`,
      summaryResponseSchema,
      { token, ...(signal ? { signal } : {}) },
    ),

  /**
   * Alertas do período, do mais recente para o mais antigo.
   *
   * Paginação por CURSOR, nunca offset — é o contrato da API, e o motivo é que
   * offset relê as linhas puladas a cada página.
   */
  alerts: (
    token: string,
    params: { from: string; to: string; kind?: string; imei?: string; cursor?: string; limit?: number },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams({ from: params.from, to: params.to });
    if (params.kind) query.set("kind", params.kind);
    if (params.imei) query.set("imei", params.imei);
    if (params.cursor) query.set("cursor", params.cursor);
    query.set("limit", String(params.limit ?? 100));
    return request(`/alerts?${query}`, alertsResponseSchema, { token, ...(signal ? { signal } : {}) });
  },

  geofences: (token: string, signal?: AbortSignal) =>
    request("/geofences", geofenceListSchema, { token, ...(signal ? { signal } : {}) }),
  createGeofence: (token: string, body: Record<string, unknown>) =>
    request("/geofences", geofenceSchema, { token, method: "POST", body }),
  /**
   * `body` vai SEM passar pelo `clean`: o `null` da cor é "voltar ao padrão", e
   * `clean` remove string vazia — que aqui seria a diferença entre apagar a cor
   * e não mexer nela.
   */
  patchGeofence: (token: string, id: string, body: Record<string, unknown>) =>
    request(`/geofences/${id}`, geofenceSchema, { token, method: "PATCH", body }),
  /** Desativa; não apaga. O histórico de alertas aponta para o id da cerca. */
  deactivateGeofence: (token: string, id: string) =>
    request(`/geofences/${id}`, okSchema, { token, method: "DELETE" }),
  geocode: (token: string, q: string, signal?: AbortSignal) =>
    request(`/geocode/search?q=${encodeURIComponent(q)}`, geocodeSchema, {
      token,
      ...(signal ? { signal } : {}),
    }),

  /** Cadastro de veículos, para o seletor do relatório. */
  vehicles: (token: string, signal?: AbortSignal) =>
    request("/vehicles", vehicleListSchema, { token, ...(signal ? { signal } : {}) }),

  /** Série recente para o gráfico. Ascendente, que é como o gráfico desenha. */
  recentTelemetry: (token: string, imei: string, hours: number, signal?: AbortSignal) => {
    const to = new Date(Date.now() + 3_600_000).toISOString();
    const from = new Date(Date.now() - hours * 3_600_000).toISOString();
    const query = `from=${from}&to=${to}&limit=500&order=desc`;
    return request(`/devices/${imei}/telemetry?${query}`, historyResponseSchema, {
      token,
      ...(signal ? { signal } : {}),
    });
  },

  /** Situação dos vínculos. 403 para quem não é `admin` — ver links.controller. */
  async deviceLinks(token: string, signal?: AbortSignal): Promise<DeviceLinksResponse> {
    return request("/device-links", deviceLinksResponseSchema, { token, ...(signal ? { signal } : {}) });
  },

  async linkDevice(
    token: string,
    imei: string,
    body: { vehicle?: string; plate?: string; speedLimit?: string },
  ): Promise<void> {
    // Campo vazio não vai: o `ValidationPipe` da API recusa string vazia onde
    // espera 1..80, e o operador veria um 400 em vez de "deixei em branco".
    const corpo = Object.fromEntries(Object.entries(body).filter(([, v]) => v !== undefined && v !== ""));
    await request(`/device-links/${imei}/link`, okSchema, { token, method: "POST", body: corpo });
  },

  async unlinkDevice(token: string, imei: string): Promise<void> {
    await request(`/device-links/${imei}/unlink`, okSchema, { token, method: "POST", body: {} });
  },
  /** Alerta pendente e motorista do momento, por equipamento. Muda devagar. */
  fleetIndicators: (token: string, signal?: AbortSignal) =>
    request("/fleet/indicators", fleetIndicatorsSchema, { token, ...(signal ? { signal } : {}) }),

  // ------------------------------------------- consumo de dados do chip
  trafficMonthly: (token: string, month: string | null, signal?: AbortSignal) =>
    request(`/traffic/monthly${month ? `?month=${month}` : ""}`, trafficMonthlySchema, {
      token,
      ...(signal ? { signal } : {}),
    }),
  trafficDaily: (token: string, from: string, to: string, imei: string | null, signal?: AbortSignal) =>
    request(
      `/traffic/daily?from=${from}&to=${to}${imei ? `&imei=${imei}` : ""}`,
      trafficDailySchema,
      { token, ...(signal ? { signal } : {}) },
    ),
};

/**
 * Cadastro (F9).
 *
 * Fica num objeto separado porque são vinte rotas de uma mesma tela — soltas
 * entre `fleetPositions` e `alerts`, elas afogariam o que o painel usa a cada
 * segundo.
 *
 * ## `null` e ausente não são a mesma coisa
 *
 * A API distingue **campo ausente** (não mexe) de **`null`** (apaga). Por isso
 * o `clean` abaixo remove `undefined` e string vazia, mas **preserva `null`**:
 * tirar o `null` junto faria "desvincular o veículo da base" virar "não mexe na
 * base", e a tela diria que salvou sem ter salvo.
 */
function clean(body: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(body).filter(([, v]) => v !== undefined && v !== ""));
}

export const registry = {
  bases: (token: string, signal?: AbortSignal) =>
    request("/bases", baseListSchema, { token, ...(signal ? { signal } : {}) }),
  createBase: (token: string, body: Record<string, unknown>) =>
    request("/bases", baseSchema, { token, method: "POST", body: clean(body) }),
  patchBase: (token: string, id: string, body: Record<string, unknown>) =>
    request(`/bases/${id}`, baseSchema, { token, method: "PATCH", body: clean(body) }),
  deleteBase: (token: string, id: string) =>
    request(`/bases/${id}`, okSchema, { token, method: "DELETE" }),

  garages: (token: string, signal?: AbortSignal) =>
    request("/garages", garageListSchema, { token, ...(signal ? { signal } : {}) }),
  // Garagem é a única escrita cuja resposta não é validada contra a schema da
  // listagem: a listagem vem de um JOIN com as chaves em camelCase e o nome da
  // base e da cerca, e a escrita devolve a linha crua. Quem chama recarrega a
  // lista depois de salvar, então validar aqui só exigiria uma segunda schema
  // para um corpo que ninguém lê.
  createGarage: (token: string, body: Record<string, unknown>) =>
    request("/garages", okSchema, { token, method: "POST", body: clean(body) }),
  patchGarage: (token: string, id: string, body: Record<string, unknown>) =>
    request(`/garages/${id}`, okSchema, { token, method: "PATCH", body: clean(body) }),
  deleteGarage: (token: string, id: string) =>
    request(`/garages/${id}`, okSchema, { token, method: "DELETE" }),

  models: (token: string, signal?: AbortSignal) =>
    request("/vehicle-models", vehicleModelListSchema, { token, ...(signal ? { signal } : {}) }),
  createModel: (token: string, body: Record<string, unknown>) =>
    request("/vehicle-models", vehicleModelSchema, { token, method: "POST", body: clean(body) }),
  patchModel: (token: string, id: string, body: Record<string, unknown>) =>
    request(`/vehicle-models/${id}`, vehicleModelSchema, { token, method: "PATCH", body: clean(body) }),
  // ------------------------------------------- condução ecológica (F12)
  ecoProfiles: (token: string, signal?: AbortSignal) =>
    request("/eco-profiles", ecoProfileListSchema, { token, ...(signal ? { signal } : {}) }),
  createEcoProfile: (token: string, body: Record<string, unknown>) =>
    request("/eco-profiles", ecoProfileSchema, { token, method: "POST", body: clean(body) }),
  patchEcoProfile: (token: string, id: string, body: Record<string, unknown>) =>
    request(`/eco-profiles/${id}`, ecoProfileSchema, { token, method: "PATCH", body: clean(body) }),
  deleteEcoProfile: (token: string, id: string) =>
    request(`/eco-profiles/${id}`, okSchema, { token, method: "DELETE" }),
  ecoRules: (token: string, id: string, signal?: AbortSignal) =>
    request(`/eco-profiles/${id}/rules`, ecoRuleListSchema, { token, ...(signal ? { signal } : {}) }),
  /** Substitui o conjunto inteiro: o perfil é um mapa, e meio mapa é pior que nenhum. */
  putEcoRules: (token: string, id: string, rules: unknown[]) =>
    request(`/eco-profiles/${id}/rules`, ecoRuleListSchema, { token, method: "PUT", body: { rules } }),
  deleteModel: (token: string, id: string) =>
    request(`/vehicle-models/${id}`, okSchema, { token, method: "DELETE" }),

  drivers: (token: string, signal?: AbortSignal) =>
    request("/drivers", driverListSchema, { token, ...(signal ? { signal } : {}) }),
  createDriver: (token: string, body: Record<string, unknown>) =>
    request("/drivers", driverSchema, { token, method: "POST", body: clean(body) }),
  patchDriver: (token: string, id: string, body: Record<string, unknown>) =>
    request(`/drivers/${id}`, driverSchema, { token, method: "PATCH", body: clean(body) }),
  deleteDriver: (token: string, id: string) =>
    request(`/drivers/${id}`, okSchema, { token, method: "DELETE" }),

  vehicles: (token: string, signal?: AbortSignal) =>
    request("/vehicles", registryVehicleListSchema, { token, ...(signal ? { signal } : {}) }),
  createVehicle: (token: string, body: Record<string, unknown>) =>
    request("/vehicles", registryVehicleSchema, { token, method: "POST", body: clean(body) }),
  patchVehicle: (token: string, id: string, body: Record<string, unknown>) =>
    request(`/vehicles/${id}`, registryVehicleSchema, { token, method: "PATCH", body: clean(body) }),
  deleteVehicle: (token: string, id: string) =>
    request(`/vehicles/${id}`, okSchema, { token, method: "DELETE" }),

  profiles: (token: string, signal?: AbortSignal) =>
    request("/profiles", profileListSchema, { token, ...(signal ? { signal } : {}) }),

  users: (token: string, signal?: AbortSignal) =>
    request("/users", userListSchema, { token, ...(signal ? { signal } : {}) }),
  createUser: (token: string, body: Record<string, unknown>) =>
    request("/users", userSchema, { token, method: "POST", body: clean(body) }),
  patchUser: (token: string, id: string, body: Record<string, unknown>) =>
    request(`/users/${id}`, okSchema, { token, method: "PATCH", body: clean(body) }),
  deleteUser: (token: string, id: string) =>
    request(`/users/${id}`, okSchema, { token, method: "DELETE" }),

  client: (token: string, signal?: AbortSignal) =>
    request("/clients/me", clientSchema, { token, ...(signal ? { signal } : {}) }),
  patchClient: (token: string, body: Record<string, unknown>) =>
    request("/clients/me", clientSchema, { token, method: "PATCH", body: clean(body) }),

  settings: (token: string, signal?: AbortSignal) =>
    request("/clients/me/settings", clientSettingsSchema, { token, ...(signal ? { signal } : {}) }),
  patchSettings: (token: string, body: Record<string, unknown>) =>
    request("/clients/me/settings", okSchema, { token, method: "PATCH", body: clean(body) }),

  /**
   * Trilha de auditoria.
   *
   * Os filtros são todos do SERVIDOR, inclusive o de data: filtrar no navegador
   * filtraria só a página já carregada, e o resultado pareceria completo.
   * `period` é resolvido no fuso do cliente, pelo relógio do servidor.
   */
  audit: (
    token: string,
    params: {
      entity?: string;
      action?: string;
      userId?: string;
      period?: string;
      from?: string;
      to?: string;
      cursor?: string;
    } = {},
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams({ limit: "100" });
    for (const key of ["entity", "action", "userId", "period", "from", "to", "cursor"] as const) {
      const value = params[key];
      if (value) query.set(key, value);
    }
    return request(`/audit?${query}`, auditResponseSchema, { token, ...(signal ? { signal } : {}) });
  },

  /** O mesmo filtro da tela, em CSV. Volta o texto; quem chama faz o `Blob`. */
  auditCsv: (token: string, params: Record<string, string | undefined>) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value) query.set(key, value);
    return requestText(`/audit/export.csv?${query}`, { token });
  },

  /** Manda o mesmo arquivo por e-mail. 503 quando o relay não está configurado. */
  auditEmail: (token: string, body: { recipients: string[] } & Record<string, unknown>) =>
    request("/audit/export/email", auditEmailSchema, { token, method: "POST", body: clean(body) }),
};

/**
 * Operação (F10) — fila de tratativas e status de comunicação.
 *
 * Separado do `registry` porque é outra tela e outro ritmo: cadastro se abre
 * uma vez por semana, a fila é o que fica aberto o dia inteiro.
 */
export const operations = {
  /**
   * A fila do dia operacional.
   *
   * `day` ausente significa "o dia corrente" — e quem decide qual é esse dia é
   * a API, no fuso do cliente, não o relógio do navegador. Um operador com o
   * computador em outro fuso veria outro dia se a conta fosse feita aqui.
   */
  queue: (
    token: string,
    params: {
      day?: string;
      status?: string;
      kind?: string;
      baseId?: string;
      garageId?: string;
      modelId?: string;
      assigneeId?: string;
      imei?: string;
      all?: boolean;
      cursor?: string;
    } = {},
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams({ limit: "200" });
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === "" || value === false) continue;
      query.set(key, String(value));
    }
    return request(`/alerts/queue?${query}`, queueResponseSchema, { token, ...(signal ? { signal } : {}) });
  },

  treat: (
    token: string,
    alertId: string,
    body: { status: string; assigneeId?: string | null; note?: string },
  ) => request(`/alerts/${alertId}/treatment`, okSchema, { token, method: "POST", body: clean(body) }),

  treatments: (token: string, alertId: string, signal?: AbortSignal) =>
    request(`/alerts/${alertId}/treatments`, treatmentHistorySchema, { token, ...(signal ? { signal } : {}) }),

  kinds: (token: string, signal?: AbortSignal) =>
    request("/alerts/kinds", alertKindsSchema, { token, ...(signal ? { signal } : {}) }),

  /**
   * Um interruptor por vez. Mandar os dois sempre faria o clique em "exige
   * ação" reescrever `enabled` com o que a tela achava que ele era — e se outra
   * pessoa tivesse mudado nesse meio-tempo, o clique desfaria a mudança dela.
   */
  patchKind: (token: string, kind: string, body: { enabled?: boolean; actionable?: boolean }) =>
    request(`/alerts/kinds/${kind}`, okSchema, { token, method: "PATCH", body }),

  // ------------------------------------------------ canais de entrega (RF-OP-08)

  channels: (token: string, signal?: AbortSignal) =>
    request("/notification-channels", channelsSchema, { token, ...(signal ? { signal } : {}) }),

  /** O `secret` da resposta só existe aqui e na rotação — ver `schemas.ts`. */
  createChannel: (
    token: string,
    body: { name: string; type: string; target: string; kinds: string[]; baseId?: string | null; active?: boolean },
  ) => request("/notification-channels", channelSchema, { token, method: "POST", body: clean(body) }),

  patchChannel: (
    token: string,
    id: string,
    body: { name?: string; target?: string; kinds?: string[]; baseId?: string | null; active?: boolean; rotateSecret?: boolean },
  ) => request(`/notification-channels/${id}`, channelSchema, { token, method: "PATCH", body: clean(body) }),

  testChannel: (token: string, id: string) =>
    request(`/notification-channels/${id}/test`, channelTestSchema, { token, method: "POST", body: {} }),

  retryChannel: (token: string, id: string) =>
    request(`/notification-channels/${id}/retry`, retrySchema, { token, method: "POST", body: {} }),

  deliveries: (token: string, id: string, signal?: AbortSignal) =>
    request(`/notification-channels/${id}/deliveries`, deliveriesSchema, { token, ...(signal ? { signal } : {}) }),

  // ------------------------------------------------------- viagens (RF-OP-06)

  trips: (
    token: string,
    params: { day?: string; baseId?: string; garageId?: string; modelId?: string; vehicleId?: string; driverId?: string; withoutDriver?: boolean } = {},
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === "" || value === false) continue;
      query.set(key, String(value));
    }
    return request(`/trips?${query}`, tripsResponseSchema, { token, ...(signal ? { signal } : {}) });
  },

  setTripDriver: (token: string, tripId: string, driverId: string | null) =>
    request(`/trips/${tripId}/driver`, okSchema, { token, method: "PATCH", body: { driverId } }),

  communication: (token: string, params: { baseId?: string } = {}, signal?: AbortSignal) => {
    const query = new URLSearchParams();
    if (params.baseId) query.set("baseId", params.baseId);
    return request(`/fleet/communication?${query}`, communicationSchema, {
      token,
      ...(signal ? { signal } : {}),
    });
  },
};
